/** /financial/subscriptions - subscription audit + cancel
 *  suggestions. Memso is read-only here: "Mark cancelled" only
 *  updates Memso's bookkeeping, it never cancels with the vendor. */
import { TeachingBanner } from "../components/TeachingBanner";
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CreditCard,
  XCircle,
} from "lucide-react";
import {
  api,
  type SubscriptionRow,
  type SubscriptionSummary,
} from "../lib/api";

function money(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(cents / 100);
}

function SubCard({
  s,
  onCancelled,
  onDismissed,
}: {
  s: SubscriptionRow;
  onCancelled: (id: string) => void;
  onDismissed: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rec = s.cancel_recommendation;
  const flagged = rec?.recommend_cancel && !rec?.dismissed;

  const act = async (kind: "cancel" | "dismiss") => {
    setBusy(true);
    try {
      if (kind === "cancel") {
        await api.subscriptionMarkCancelled(s.id);
        onCancelled(s.id);
      } else {
        await api.subscriptionDismissRecommendation(s.id);
        onDismissed(s.id);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40"
      >
        <CreditCard className="w-5 h-5 shrink-0 text-slate-500" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {s.vendor_name}
            </span>
            {flagged && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-3 h-3" /> Consider cancelling
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {money(s.amount_cents, s.currency)} / {s.cadence} · last charge{" "}
            {s.last_charge_at
              ? new Date(s.last_charge_at).toLocaleDateString()
              : "unknown"}{" "}
            · via {s.detection_source}
          </p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100 dark:border-slate-800 text-sm">
          {rec?.recommend_cancel && (
            <div className="rounded-lg bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2 text-amber-700 dark:text-amber-300">
              {rec.reason} (confidence{" "}
              {Math.round((rec.confidence ?? 0) * 100)}%)
            </div>
          )}
          {s.usage_signals && (
            <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-0.5">
              <li>
                Mentions (30d): {s.usage_signals.mentions_count_30d} · (90d):{" "}
                {s.usage_signals.mentions_count_90d}
              </li>
              <li>
                Last vendor traffic:{" "}
                {s.usage_signals.last_email_from_vendor_at
                  ? new Date(
                      s.usage_signals.last_email_from_vendor_at,
                    ).toLocaleDateString()
                  : "none seen"}
              </li>
              <li>First seen: {new Date(s.first_seen_at).toLocaleDateString()}</li>
            </ul>
          )}
          <div className="flex gap-2">
            <button
              disabled={busy}
              onClick={() => act("cancel")}
              className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
            >
              <XCircle className="w-3.5 h-3.5" /> Mark cancelled
            </button>
            {flagged && (
              <button
                disabled={busy}
                onClick={() => act("dismiss")}
                className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
              >
                Dismiss recommendation
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function Subscriptions() {
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [sum, list] = await Promise.all([
        api.subscriptionsSummary(),
        api.subscriptions(true),
      ]);
      setSummary(sum);
      setRows(list.subscriptions);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const drop = (id: string) =>
    setRows((cur) => cur.filter((r) => r.id !== id));

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <TeachingBanner id="subscriptions_overview" />
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          Subscriptions
        </h1>
        {summary && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Total monthly burn:{" "}
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {money(summary.total_monthly_cents)}/mo
            </span>{" "}
            across {summary.count} subscription
            {summary.count === 1 ? "" : "s"}.
          </p>
        )}
      </div>

      {loading && (
        <p className="text-sm text-slate-400">Loading subscriptions...</p>
      )}
      {err && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
      )}
      {!loading && !err && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center">
          <CreditCard className="w-8 h-8 mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            No subscriptions detected yet. The detector runs in the
            background once enabled.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((s) => (
          <SubCard
            key={s.id}
            s={s}
            onCancelled={drop}
            onDismissed={() => void load()}
          />
        ))}
      </div>
    </div>
  );
}
